import { accessAPI } from "../api/client";
import { ACTIVE_PAGES } from "../constants/pageMaster";
import { hasPermission, hasRole, normalizeRole } from "./accessControl";

// GLOBAL PORTAL DEFAULT GRANTS
// Mirror of the backend GLOBAL_DEFAULT_GRANTS (accessEnforcer.js). These
// page.actions are defaults for every authenticated user unless the effective
// snapshot contains an explicit employee DENY. Keep this set in sync with the backend.
const GLOBAL_DEFAULT_GRANTS = new Set([
  "delegation.view",
  "delegation.manage",
  "ai.use",
  "support_ticket.view",
  "support_ticket.create",
  "reimbursement.view",
  "reimbursement.create",
]);

export function isGlobalDefaultGrant(page, action) {
  return GLOBAL_DEFAULT_GRANTS.has(`${page}.${action}`);
}

// In-memory snapshot of the logged-in user's employee-specific access, loaded
// from GET /api/access/my-access after login. Stored as:
//   { configured: bool, pages: { pageKey: { actionKey: bool, ... } } }
let accessSnapshot = null;

// Bump whenever the snapshot shape/endpoint contract changes so browsers never
// reuse a stale cached snapshot (e.g. an empty `{pages:{}}` written by an
// older build when /my-access failed — that exact stale-empty case hid every
// granted page for No Role users).
const ACCESS_STORE_KEY = "hrportal_user_access_v5";
const OLD_ACCESS_STORE_KEYS = ["hrportal_user_access", "hrportal_user_access_v2", "hrportal_user_access_v3", "hrportal_user_access_v4"];

function readStoredSnapshot() {
  try {
    const raw = localStorage.getItem(ACCESS_STORE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeStoredSnapshot(snap) {
  try {
    localStorage.setItem(ACCESS_STORE_KEY, JSON.stringify(snap));
  } catch {
    // storage full / private mode — in-memory snapshot still works
  }
  for (const oldKey of OLD_ACCESS_STORE_KEYS) {
    try {
      localStorage.removeItem(oldKey);
    } catch {
      // ignore
    }
  }
}

export function getStoredAccess() {
  if (accessSnapshot) return accessSnapshot;
  accessSnapshot = readStoredSnapshot();
  return accessSnapshot;
}

export async function loadUserAccess() {
  // Drop any cached snapshot FIRST so a failed /my-access call below can never
  // leave the previous (possibly stale/empty) permissions in place. A failed
  // load then falls back to role-based guards (snapshot null) instead of a
  // stale matrix that could hide freshly granted No Role permissions.
  accessSnapshot = null;
  try {
    const res = await accessAPI.getMine();
    if (res.data && res.data.pages) {
      accessSnapshot = res.data;
      writeStoredSnapshot(accessSnapshot);
    } else {
      for (const oldKey of OLD_ACCESS_STORE_KEYS) {
        try {
          localStorage.removeItem(oldKey);
        } catch {
          // ignore
        }
      }
      try {
        localStorage.removeItem(ACCESS_STORE_KEY);
      } catch {
        // ignore
      }
      accessSnapshot = null;
    }
    return accessSnapshot;
  } catch (err) {
    console.error("Failed to load user access", err);
    accessSnapshot = null;
    return null;
  }
}

export function clearUserAccess() {
  accessSnapshot = null;
  for (const oldKey of [...OLD_ACCESS_STORE_KEYS, ACCESS_STORE_KEY]) {
    try {
      localStorage.removeItem(oldKey);
    } catch {
      // ignore
    }
  }
}

// A user may access a page when VIEW is allowed by the effective snapshot or
// by a global default. An explicit employee DENY in the snapshot wins.
export function mayViewPage(page) {
  const snap = getStoredAccess();
  const value = snap?.pages?.[page]?.view;
  if (value === false) return false;
  if (isGlobalDefaultGrant(page, "view")) return true;
  return value === true;
}

// A user may perform a specific action within a page. Global portal defaults
// are always allowed, regardless of the snapshot.
export function mayPerform(page, action) {
  const snap = getStoredAccess();
  const value = snap?.pages?.[page]?.[action];
  // The effective snapshot includes explicit employee DENY. Respect it before
  // applying a frontend global default grant.
  if (value === false) return false;
  if (isGlobalDefaultGrant(page, action)) return true;
  return value === true;
}

// Explicit employee DENY for a page.action key. Stored as user_access rows
// with permission='deny'. Frontend snapshots encode DENY as `false`.
export function isDenied(page, action) {
  const snap = getStoredAccess();
  if (!snap || !snap.pages || !snap.pages[page]) return false;
  return snap.pages[page][action] === false;
}

// Combined page/action gate used by page components. For the six legacy system
// roles the original behaviour is preserved: the legacy permission AND the
// matrix must BOTH allow. No-role ("No Role") and custom-role users have no
// meaningful legacy permission set, so the matrix (employee overrides + role
// permissions) is the only gate — this lets employee-specific grants surface
// the Add/Edit/Delete buttons instead of hard-hiding them.
export function mayPerformWithRole(userRole, page, action, legacyPermission) {
  // Explicit employee DENY wins over everything.
  if (isDenied(page, action)) return false;
  // Explicit employee ALLOW wins next — this is the entire No Role model
  // (the user has no legacy permissions, so the matrix is the only gate).
  if (mayPerform(page, action) === true) return true;
  if (normalizeRole(userRole) === "unknown") return mayPerform(page, action);
  if (hasPermission(userRole, legacyPermission)) return mayPerform(page, action);
  return false;
}

// Map a route path to its access page key. This is used by App.js to hide
// sidebar items and gate routes when a user has employee-specific access
// configured. super_admin is always exempt (see handleUserAccessRouting).
//
// DERIVED from the Page Master (src/constants/pageMaster.js) — the single
// frontend source for pageKey/route metadata. Do not hand-edit pairs here;
// edit pageMaster.js instead. Keys remain stable regardless of sidebar
// order, position, route changes or division grouping.
export const ROUTE_TO_ACCESS_PAGE = Object.fromEntries(
  ACTIVE_PAGES.map((p) => [p.route, p.pageKey])
);

// Return the access page key for a given path (with param suffixes stripped).
export function routeToAccessPage(pathname) {
  const base = "/" + String(pathname || "").split("/")[1];
  return ROUTE_TO_ACCESS_PAGE[base] || null;
}

// Visibility gate for a navigation item shared by the Sidebar and the Home
// page, so both surfaces always resolve the exact same set of accessible
// pages. The rules (in priority order) mirror the backend access resolver:
//   1. Global portal defaults (e.g. Delegations, AI, Support Tickets) are
//      always visible regardless of role, snapshot or employee overrides.
//   2. Once the employee access snapshot is loaded it is the ONLY gate: it
//      already resolves employee overrides + role + matrix defaults.
//   3. Before the snapshot arrives, fall back to the legacy role permission
//      (or role allow-list), preserving current first-frames behaviour.
export function canAccessNavItem(role, item) {
  const pageKey = routeToAccessPage(item.to);
  const snap = getStoredAccess();
  if (snap && snap.pages) {
    if (pageKey) return mayViewPage(pageKey);
  }
  // Before the effective snapshot arrives, use global defaults as a fallback.
  if (pageKey && isGlobalDefaultGrant(pageKey, "view")) return true;
  if (item.permission) return hasPermission(role, item.permission);
  if (Array.isArray(item.roles) && item.roles.length > 0) return hasRole(role, item.roles);
  return true;
}

// Employee-specific permission-aware route access (single source, shared by
// the Sidebar and Home). Returns true when the user may open the route's page.
//   - super_admin: always allowed.
//   - snapshot present: allowed only if the page's effective VIEW is allowed
//     (the snapshot already resolves employee overrides + role permissions).
//   - no snapshot yet (just logged in): allowed; existing role-based gates
//     (canAccessNavItem / PermissionGuard) still apply.
export function canAccessRouteByUser(role, pathname) {
  if (hasRole(role, ["super_admin"])) return true;
  const snap = getStoredAccess();
  if (!snap) return true; // snapshot not loaded yet — fallback to role-based checks
  const page = routeToAccessPage(pathname);
  if (!page) return true; // not part of the access matrix
  return mayViewPage(page);
}
